import { useState } from 'react';

interface PdfPageState {
	documentKey: string;
	totalPages: number;
	currentPage: number;
}

const initialState = (documentKey: string): PdfPageState => ({
	documentKey,
	totalPages: 0,
	currentPage: 1,
});

/**
 * Page count and position for one pdf document, discarded when the document changes.
 *
 * The reset has to land in the same render as the new key, not in an effect:
 * `<Document>` renders its children the moment its own pdf state is truthy, so a
 * page count left over from the previous document produces one committed render of
 * `<Page pageNumber={n}>` for pages the new document doesn't have — 18 error tiles
 * when a 20-page document is swapped for a 2-page one — and a header reading
 * "8 / 2" (DC-1069).
 */
export const usePdfPageState = (documentKey: string) => {
	const [state, setState] = useState(() => initialState(documentKey));

	const current = state.documentKey === documentKey ? state : initialState(documentKey);
	if (current !== state) {
		setState(current);
	}

	// Both setters return `previous` untouched when the value has not moved.
	// Callers re-report the page from a scroll handler that fires far more often
	// than the page changes, and a new state object per redundant report denies
	// React its bail-out and renders forever.
	const setTotalPages = (totalPages: number) => {
		setState((previous) =>
			previous.totalPages === totalPages ? previous : { ...previous, totalPages }
		);
	};

	const setCurrentPage = (currentPage: number) => {
		setState((previous) =>
			previous.currentPage === currentPage ? previous : { ...previous, currentPage }
		);
	};

	return {
		totalPages: current.totalPages,
		currentPage: current.currentPage,
		setTotalPages,
		setCurrentPage,
	};
};
